import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { CheckboxEditor, TextFieldEditor } from '../';
import * as osmHelpers from './osmHelpers';

import s from './OSMHoursEditor.module.scss';

const initialState = () => ({
    selectedDays: [],
    opens: null,
    closes: null,
    is24hours: false,
    opensValid: false,
    closesValid: false,
});

const arr = configuredLinesString => _.compact(_.split(configuredLinesString, ';'));

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export default class OSMHoursEditor extends Component {
    static propTypes = {
        className: PropTypes.string,
        configuredLinesString: PropTypes.string,
        disabled: PropTypes.bool,
        onChange: PropTypes.func,
    };

    static defaultProps = {
        className: '',
        configuredLinesString: '',
        disabled: false,
        onChange: () => {},
    };

    constructor(props) {
        super(props);
        this.state = { ...initialState(), configuredLines: arr(props.configuredLinesString) };
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        if (newProps.configuredLinesString !== this.props.configuredLinesString) {
            this.setState({ configuredLines: arr(newProps.configuredLinesString) });
        }
    }

    toggleDaySelection = day => {
        if (this.props.disabled) return;

        this.setState(state => {
            const remove = _.includes(state.selectedDays, day);
            const newSelectedDays = remove ? _.without(state.selectedDays, day) : _.concat(state.selectedDays, day);
            return { selectedDays: newSelectedDays };
        });
    };

    classesForDayBox = day => {
        const { selectedDays } = this.state;
        const isSelected = _.includes(selectedDays, day);
        return classNames(s.dayButton, { [s.selected]: isSelected });
    };

    addLine = () => {
        const { selectedDays, opens, closes, opensValid, closesValid } = this.state;
        // add button does nothing if valid times aren't entered
        if (!(opensValid && closesValid && selectedDays.length > 0)) {
            return;
        }

        const { configuredLines } = this.state;
        const newLines = osmHelpers.userInputToSimplifiedOSMStrings(selectedDays, opens, closes);
        _.forEach(newLines, newLine => {
            if (!_.includes(configuredLines, newLine)) {
                configuredLines.push(newLine);
                this.props.onChange(_.join(configuredLines, '; '));
            }
        });
        this.setState(initialState());
    };

    setAllDays = isAllDays => {
        const selectedDays = isAllDays ? osmHelpers.DAYS.slice(0) : [];
        this.setState({ selectedDays });
    };

    set24hours = is24hours => {
        if (is24hours) {
            this.setState({
                is24hours: true,
                opens: osmHelpers.toUserFriendlyTimeString(0, true),
                closes: osmHelpers.toUserFriendlyTimeString(0, false),
                opensValid: true,
                closesValid: true,
            });
        } else {
            this.setState({
                is24hours: false,
                opens: '',
                closes: '',
                opensValid: false,
                closesValid: false,
            });
        }
    };

    deleteLine = lineText => {
        const { configuredLines } = this.state;
        const { onChange } = this.props;
        const newLines = _.pull(configuredLines, lineText);
        onChange(_.join(newLines, ';'));
    };

    onDragEnd = result => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const reorderedLines = reorder(this.state.configuredLines, result.source.index, result.destination.index);

        this.props.onChange(_.join(reorderedLines, ';'));
    };

    updateTime = (isOpens, time) => {
        const newState = { is24hours: false };
        const fieldName = isOpens ? 'opens' : 'closes';
        newState[fieldName] = time;
        newState[`${fieldName}Valid`] = osmHelpers.validateTimeString(time, isOpens);
        this.setState(newState);
    };

    standardizeTime = isOpens => {
        const currentTime = isOpens ? this.state.opens : this.state.closes;
        if (currentTime && osmHelpers.validateTimeString(currentTime, isOpens)) {
            const newTime = osmHelpers.toUserFriendlyTimeString(currentTime, isOpens);
            const fieldName = isOpens ? 'opens' : 'closes';
            this.setState({ [fieldName]: newTime });
        }
    };

    renderLine = (lineText, index) => {
        const { disabled } = this.props;
        const lineTextParts = _.compact(_.split(lineText, ' '));
        const days = lineTextParts.length > 0 ? osmHelpers.osmDaysToPrettyDays(lineTextParts[0]) : '*** invalid ***';
        const hours = lineTextParts.length > 1 ? osmHelpers.osmHoursToPrettyHours(lineTextParts[1]) : '';

        return (
            <Draggable key={lineText} draggableId={lineText} index={index} isDragDisabled={disabled}>
                {provided => (
                    <div>
                        <div className={s.configuredLine} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <div className={s.configuredDays}>{days}</div>
                            <div className={s.configuredHours}>{hours}</div>
                            <div className={classNames(s.moveLine, 'icon-hamburger-menu')} />
                            <div className={classNames(s.deleteLine, 'icon-close')} onClick={() => this.deleteLine(lineText)} />
                        </div>
                        {provided.placeholder}
                    </div>
                )}
            </Draggable>
        );
    };

    render = () => {
        const { className, disabled } = this.props;
        const { configuredLines } = this.state;
        const { selectedDays, opens, closes, opensValid, closesValid, is24hours } = this.state;
        const disableHours = selectedDays.length === 0;
        const allowAdd = selectedDays.length > 0 && opensValid && closesValid;
        const inputErrorMessage = 'Hours are restricted to the 12-hour format and by 15 minute intervals (e.g. 7:15am, 9:30pm, 12:45pm)';
        const invalidOpen = opens && !opensValid;
        const invalidClose = closes && !closesValid;

        return (
            <div className={classNames(s.avfHoursEditor, className)}>
                <div className={s.checkboxWrapper}>
                    <CheckboxEditor label="Open all days" value={selectedDays.length === 7} onValueChanged={this.setAllDays} disabled={disabled} />
                </div>
                <div className={s.daysWrapper}>
                    {osmHelpers.DAYS.map(day => (
                        <div key={day.value} className={this.classesForDayBox(day)} onClick={() => this.toggleDaySelection(day)}>
                            {day.label}
                        </div>
                    ))}
                </div>

                <div className={classNames(s.hours)}>
                    <div className={s.hoursWrapper}>
                        <TextFieldEditor
                            className={s.opens}
                            label="OPENS"
                            placeHolder="10:00 am"
                            value={this.state.opens}
                            onChange={time => {
                                this.updateTime(true, time);
                            }}
                            invalid={invalidOpen}
                            onBlur={() => this.standardizeTime(true)}
                            disabled={disableHours || disabled}
                        />
                        <TextFieldEditor
                            className={s.closes}
                            label="CLOSES"
                            placeHolder="5:00 pm"
                            value={this.state.closes}
                            onChange={time => {
                                this.updateTime(false, time);
                            }}
                            invalid={invalidClose}
                            onBlur={() => this.standardizeTime(false)}
                            disabled={disableHours || disabled}
                        />
                        <div className={classNames(s.addButton, 'icon-add', { [s.disabled]: !allowAdd })} onClick={this.addLine} />
                    </div>
                    {(invalidOpen || invalidClose) && <div className={s.inputErrorMessage}>{inputErrorMessage}</div>}
                    <div className={s.checkboxWrapper}>
                        <CheckboxEditor label="Open 24 hours" value={is24hours} onValueChanged={this.set24hours} disabled={disableHours || disabled} />
                    </div>
                </div>

                <DragDropContext onDragEnd={this.onDragEnd} className={s.configuredLines}>
                    <Droppable droppableId="droppable" isDropDisabled={disabled}>
                        {provided => <div ref={provided.innerRef}>{configuredLines.map(this.renderLine)}</div>}
                    </Droppable>
                </DragDropContext>
            </div>
        );
    };
}
